import React, { useEffect, useMemo, useRef, useState } from 'react';
import { arrayOf, bool, func, oneOf, shape, string } from 'prop-types';
import { cx } from '@emotion/css';

import { colors } from '@/colors';
import { styContainer, stySelector, stySegment } from './style';

const BUY_SELL_SEGMENTS = [
  { label: 'Beli', value: 'BUY', background: colors.AJAIB_BLUE_5 },
  { label: 'Jual', value: 'SELL', background: colors.RED_5 },
];

const SegmentedControl = (props) => {
  const {
    block,
    defaultValue,
    value: propsValue,
    onChange,
    segments: propsSegments,
    style,
    variant,
    ...rest
  } = props;

  const containerRef = useRef(null);
  const segmentRefs = useRef([]);
  const [segments, setSegments] = useState([]);
  const [value, setValue] = useState(defaultValue);

  // Store value on state when component is uncontrolled
  const isControlled = useMemo(() => propsValue !== null, [propsValue]);

  const activeIndex = useMemo(
    () => segments?.findIndex((v) => v.value === value),
    [segments, value]
  );

  useEffect(() => {
    // Init segments
    if (variant === 'buy-sell') {
      // For buy-sell variant, use hardcoded segments
      setSegments(BUY_SELL_SEGMENTS);
    } else {
      setSegments(propsSegments);
    }
  }, [variant, propsSegments]);

  useEffect(() => {
    if (isControlled) {
      setValue(propsValue);
    }
  }, [isControlled, propsValue]);

  const handleChange = (_value, i) => {
    const objValue = segments?.find((v) => v.value === _value);
    if (isControlled) onChange(objValue, i);
    else setValue(objValue?.value);
  };

  const handleKeyDown = (event, i) => {
    if (!segments[i].disabled) {
      if (event.key === 'Enter') {
        handleChange(segments[i].value, i);
      }
    }
  };

  const styProps = {
    block,
    variant,
  };

  const stySelectorProps = {
    width: segmentRefs.current?.[activeIndex]?.offsetWidth,
    left: segmentRefs.current?.[activeIndex]?.offsetLeft,
    background: segments[activeIndex]?.background,
  };

  return (
    <div className={styContainer(styProps)} style={style} ref={containerRef} {...rest}>
      <div className={stySelector(stySelectorProps)} />
      {segments?.map((item, i) => (
        <div
          ref={(el) => {
            segmentRefs.current[i] = el;
          }}
          key={item.value}
          role="button"
          className={cx(stySegment(styProps), { active: item.value === value })}
          tabIndex="0"
          onClick={() => handleChange(item.value, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
        >
          <span>{item.label}</span>
        </div>
      ))}
    </div>
  );
};

SegmentedControl.propTypes = {
  block: bool,
  defaultValue: string,
  onChange: func,
  segments: arrayOf(
    shape({
      label: string.isRequired,
      value: string.isRequired,
      disabled: bool,
      background: string,
    })
  ),
  style: shape({}),
  value: string,
  variant: oneOf(['primary', 'buy-sell']),
};

SegmentedControl.defaultProps = {
  block: false,
  defaultValue: null,
  onChange: () => {},
  segments: [],
  style: {},
  value: null,
  variant: 'primary',
};

export default SegmentedControl;
